import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

const C = {
  bg: "#030706",
  panel: "#080b08",
  card: "#090d0b",
  line: "rgba(247,201,72,0.25)",
  gold: "#f7c948",
  dim: "#8a6d25",
  green: "#22c55e",
  red: "#ef4444",
  text: "#f8e38a",
};

function fmt(value, digits = 2) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toFixed(digits) : "-";
}

function compact(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "-";
  if (Math.abs(number) >= 10000000) return `${(number / 10000000).toFixed(2)}Cr`;
  if (Math.abs(number) >= 100000) return `${(number / 100000).toFixed(2)}L`;
  if (Math.abs(number) >= 1000) return `${(number / 1000).toFixed(1)}K`;
  return number.toFixed(0);
}

function moneyCr(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? `${(number / 10000000).toFixed(2)}Cr` : "-";
}

function timeToMinutes(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return hour * 60 + minute;
}

function minuteLabel(totalMinutes) {
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
}

function buildIntervalRows(snapshots, intervalMinutes) {
  const start = timeToMinutes("09:15");
  const end = timeToMinutes("15:30");
  const byMinute = new Map();

  (snapshots || []).forEach(snapshot => {
    if (snapshot?.minute) byMinute.set(snapshot.minute, snapshot);
  });

  return Array.from({ length: Math.ceil((end - start) / intervalMinutes) }, (_, position) => {
    const intervalStart = start + position * intervalMinutes;
    const intervalEnd = Math.min(intervalStart + intervalMinutes, end);
    const records = [];

    for (let minute = intervalStart + 1; minute <= intervalEnd; minute += 1) {
      const record = byMinute.get(minuteLabel(minute));
      if (record) records.push(record);
    }

    const rows = records.flatMap(record => record.rows || []);
    const ceRows = rows.filter(row => row.optionSide === "CE");
    const peRows = rows.filter(row => row.optionSide === "PE");

    return {
      serial: position + 1,
      label: `${minuteLabel(intervalStart)} - ${minuteLabel(intervalEnd)}`,
      records,
      rows,
      ceRows,
      peRows,
      totalAmount: rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      totalActive: rows.reduce((sum, row) => sum + Number(row.remainingAmount || 0), 0),
    };
  });
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded border px-4 py-2 text-sm font-bold"
      style={{
        borderColor: active ? C.gold : C.line,
        background: active ? "#2a2108" : C.card,
        color: active ? C.gold : C.dim,
      }}
    >
      {children}
    </button>
  );
}

function PunchColumn({ title, rows, emptyText }) {
  const totalActive = rows.reduce((sum, row) => sum + Number(row.remainingAmount || 0), 0);
  const netAmount = rows.reduce((sum, row) => {
    const amount = Number(row.remainingAmount || row.amount || 0);
    return row.side === "SELL" ? sum - amount : sum + amount;
  }, 0);
  const netText = netAmount > 0
    ? `+${moneyCr(netAmount)} added`
    : netAmount < 0
      ? `-${moneyCr(Math.abs(netAmount))} left`
      : "no fresh change";

  return (
    <div className="rounded border p-5" style={{ background: C.panel, borderColor: C.line }}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xl font-bold" style={{ color: C.gold }}>{title}</h3>
        <div className="text-xs font-bold" style={{ color: C.gold }}>{rows.length} active</div>
      </div>
      <div className="mb-4 grid gap-3 md:grid-cols-2">
        <div className="rounded border px-4 py-3" style={{ background: C.card, borderColor: C.line }}>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: C.dim }}>Total Still Active</div>
          <div className="mt-1 text-lg font-bold" style={{ color: C.gold }}>{moneyCr(totalActive)}</div>
        </div>
        <div className="rounded border px-4 py-3" style={{ background: C.card, borderColor: C.line }}>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: C.dim }}>Net Added / Left</div>
          <div className="mt-1 text-lg font-bold" style={{ color: netAmount < 0 ? C.red : netAmount > 0 ? C.green : C.dim }}>
            {netText}
          </div>
        </div>
      </div>
      <div className="overflow-auto rounded border" style={{ borderColor: C.line }}>
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead style={{ background: "#078574", color: C.gold }}>
            <tr>
              {["Strike", "Amount", "Still Active", "Quantity", "Holding Qty", "Price"].map(head => (
                <th key={head} className="border px-3 py-3 text-left" style={{ borderColor: C.line }}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const color = row.side === "BUY" ? C.green : C.red;
              return (
                <tr key={`${row.id}-${row.time || row.capturedAt || ""}`} style={{ background: C.card, color }}>
                  <td className="border px-3 py-3 font-bold" style={{ borderColor: C.line }}>{row.label}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.gold }}>{moneyCr(row.amount)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{moneyCr(row.remainingAmount)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{compact(row.quantity)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{compact(row.remainingQuantity)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{fmt(row.price)}</td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={6} className="border px-3 py-4" style={{ borderColor: C.line, color: C.dim }}>
                  {emptyText || `No ${title} rows available.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SavedIntervals({ snapshots, intervalMinutes }) {
  const intervalRows = useMemo(
    () => buildIntervalRows(snapshots, intervalMinutes),
    [snapshots, intervalMinutes]
  );
  const latestInterval = useMemo(
    () => [...intervalRows].reverse().find(row => row.rows.length),
    [intervalRows]
  );
  const [expandedInterval, setExpandedInterval] = useState("");

  useEffect(() => {
    if (latestInterval) setExpandedInterval(latestInterval.label);
  }, [latestInterval]);

  return (
    <div className="overflow-auto rounded border" style={{ borderColor: C.line }}>
      <table className="w-full min-w-[980px] border-collapse text-sm">
        <thead style={{ background: "#078574", color: C.gold }}>
          <tr>
            {["#", "Interval", "Rows", "CE", "PE", "Amount", "Still Active"].map(head => (
              <th key={head} className="border px-3 py-3 text-left" style={{ borderColor: C.line }}>{head}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {intervalRows.map(row => {
            const isExpanded = expandedInterval === row.label;
            return (
              <React.Fragment key={row.label}>
                <tr
                  onClick={() => row.rows.length && setExpandedInterval(current => current === row.label ? "" : row.label)}
                  style={{
                    background: row.rows.length ? "rgba(20, 16, 16, 0.88)" : "rgba(255,255,255,0.015)",
                    color: row.rows.length ? C.gold : C.dim,
                    cursor: row.rows.length ? "pointer" : "default",
                  }}
                >
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.serial}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.label}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.rows.length || "--"}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.ceRows.length || "--"}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.peRows.length || "--"}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.rows.length ? moneyCr(row.totalAmount) : "--"}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.rows.length ? moneyCr(row.totalActive) : "--"}</td>
                </tr>
                {isExpanded && row.rows.length > 0 && (
                  <tr>
                    <td colSpan={7} className="border p-4" style={{ borderColor: C.line, background: "#050806" }}>
                      <div className="grid gap-5 xl:grid-cols-2">
                        <PunchColumn title="CE" rows={row.ceRows} />
                        <PunchColumn title="PE" rows={row.peRows} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function DhanBigMoneyFlow() {
  const [activeTab, setActiveTab] = useState("options");
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [collectionEnabled, setCollectionEnabled] = useState(false);
  const [controlLoading, setControlLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ceRows = (snapshot?.rows || []).filter(row => row.optionSide === "CE");
  const peRows = (snapshot?.rows || []).filter(row => row.optionSide === "PE");
  const dateOptions = history.map(record => record.date);
  const selectedHistory = history.find(record => record.date === selectedDate) || history[0] || null;
  const savedTab = activeTab === "1" || activeTab === "5";

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/dhan-big-money-flow/history?t=${Date.now()}`, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Dhan Big Money history failed");
      setHistory(data.history || []);
      if (!selectedDate && data.history?.[0]?.date) setSelectedDate(data.history[0].date);
    } catch {
      setHistory([]);
    }
  };

  const loadSnapshot = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/dhan-big-money-flow/snapshot?t=${Date.now()}`, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Dhan Big Money Flow fetch failed");
      setSnapshot(data);
      await loadHistory();
    } catch (err) {
      setError(err.message || "Dhan Big Money Flow fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/dhan-big-money-flow/status?t=${Date.now()}`, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Dhan Big Money status failed");
      setCollectionEnabled(data.collectionEnabled === true);
      if (data.lastCollectionError) setError(data.lastCollectionError);
      return data;
    } catch {
      setCollectionEnabled(false);
      return null;
    }
  };

  const toggleCollection = async () => {
    const nextEnabled = !collectionEnabled;
    setControlLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/dhan-big-money-flow/control`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.collectionEnabled !== undefined) setCollectionEnabled(data.collectionEnabled === true);
      if (!res.ok || !data.success) throw new Error(data.error || "Dhan Big Money control failed");
      if (data.snapshot) setSnapshot(data.snapshot);
      await loadHistory();
    } catch (err) {
      setError(err.message || "Dhan Big Money control failed");
    } finally {
      setControlLoading(false);
    }
  };

  useEffect(() => {
    const initialLoad = async () => {
      const status = await loadStatus();
      await loadHistory();
      if (status?.collectionEnabled) await loadSnapshot();
    };
    initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!collectionEnabled) return undefined;
    const timer = setInterval(loadSnapshot, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionEnabled]);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, padding: 26 }}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.42em]" style={{ color: C.dim }}>
            Secret Page Dhan Credentials
          </div>
          <h1 className="mt-2 text-3xl font-bold" style={{ color: C.gold }}>
            DHAN BIG MONEY FLOW
          </h1>
          <div className="mt-2 text-sm" style={{ color: C.text }}>
            Auto fetch every 5 seconds | Saved 1 Min / 5 Min intervals | Last 14 saved days
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={toggleCollection}
            disabled={controlLoading}
            className="rounded border px-4 py-2 text-sm font-bold"
            style={{
              borderColor: collectionEnabled ? C.red : C.green,
              background: collectionEnabled ? "#3d1010" : "#063c2c",
              color: collectionEnabled ? "#fecaca" : "#d1fae5",
              opacity: controlLoading ? 0.65 : 1,
            }}
          >
            {collectionEnabled ? "Stop" : "Start"}
          </button>
          <button
            type="button"
            onClick={loadSnapshot}
            className="rounded border px-4 py-2 text-sm font-bold"
            style={{ borderColor: C.line, background: C.card, color: C.gold }}
          >
            <RefreshCw size={15} className="mr-2 inline" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded border px-4 py-3 text-sm" style={{ borderColor: "rgba(239,68,68,0.5)", color: C.red }}>
          {error}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <TabButton active={activeTab === "options"} onClick={() => setActiveTab("options")}>CE / PE</TabButton>
        <TabButton active={activeTab === "1"} onClick={() => setActiveTab("1")}>1 Min</TabButton>
        <TabButton active={activeTab === "5"} onClick={() => setActiveTab("5")}>5 Min</TabButton>
        {savedTab && (
          <select
            value={selectedHistory?.date || ""}
            onChange={event => setSelectedDate(event.target.value)}
            className="rounded border bg-black px-4 py-2 text-sm font-bold"
            style={{ borderColor: C.line, color: C.gold }}
          >
            {dateOptions.map(date => <option key={date} value={date}>{date}</option>)}
            {!dateOptions.length && <option value="">No saved dates</option>}
          </select>
        )}
      </div>

      <div className="mt-5">
        {activeTab === "options" && (
          <div className="grid gap-5 xl:grid-cols-2">
            <PunchColumn title="CE" rows={ceRows} emptyText="No Dhan CE rows loaded yet." />
            <PunchColumn title="PE" rows={peRows} emptyText="No Dhan PE rows loaded yet." />
          </div>
        )}

        {savedTab && (
          <SavedIntervals
            snapshots={selectedHistory?.minuteSnapshots || []}
            intervalMinutes={activeTab === "5" ? 5 : 1}
          />
        )}
      </div>

      {loading && (
        <div className="mt-4 text-sm" style={{ color: C.dim }}>
          Fetching Dhan option-chain snapshot...
        </div>
      )}
    </div>
  );
}
